import { inject, Injectable } from '@angular/core';
import type { LocationInput } from '@app/pages/cmms/interfaces/cmms-global.interfaces';
import { TranslateService } from '@tolgee/ngx';
import { Store } from '@ngrx/store';
import { buildingsList } from '@app/layout/state/layout.selectors';
import { toSignal } from '@angular/core/rxjs-interop';

export interface Location {
	type?: string;
	locationType?: string;
	locationId?: string;
	id?: string;
}

@Injectable({ providedIn: 'root' })
export class LocationService {
	private readonly translateService = inject(TranslateService);
	private readonly store$ = inject(Store);

	buildings$ = this.store$.select(buildingsList);
	private buildings = toSignal(this.buildings$, { initialValue: [] });

	formatLocationName(ancestry: LocationInput[]): string {
		if (ancestry.length < 3) {
			return `${ancestry[1].name.substring(0, 10)}${ancestry[1].name.length > 10 ? '...' : ''} | ${ancestry[0].name}`;
		}

		const grandparent = ancestry[2].name;
		const parent = ancestry[1].name;

		if (grandparent.length > 10) {
			return `${grandparent.substring(0, 10)}... | ${ancestry[0].name}`;
		}
		const remainingLength = 10 - grandparent.length;
		const truncatedName1 = parent.substring(0, remainingLength);

		return `${grandparent} | ${truncatedName1}${parent.length > remainingLength ? '...' : ''} | ${ancestry[0].name}`;
	}

	findBuildingIdFromLocation(location: Location): string | undefined {
		const locationId = location.locationId ?? location.id;

		if (!locationId) {
			return undefined;
		}

		if (location.type === 'building' || location.locationType === 'Building') {
			return locationId;
		}

		if (location.type === 'floor' || location.locationType === 'Floor') {
			return this.getBuildingIdFromFloor(locationId);
		}

		if (location.type === 'room' || location.locationType === 'Room') {
			return this.getBuildingIdFromRoom(locationId);
		}

		return undefined;
	}

	getBuildingIdFromFloor(floorId: string): string | undefined {
		if (!floorId || !this.buildings()?.length) {
			return undefined;
		}

		for (let i = 0; i < this.buildings().length; i++) {
			if (this.buildings()[i].floor.some(floor => floor.id === floorId)) {
				return this.buildings()[i].id;
			}
		}

		console.error(this.translateService.instant('no_building_found_for_floor'));
		return undefined;
	}

	getBuildingIdFromRoom(roomId: string): string | undefined {
		if (!roomId || !this.buildings()?.length) {
			return undefined;
		}

		for (let i = 0; i < this.buildings().length; i++) {
			if (
				this.buildings()[i].floor.some(floor =>
					floor.room.some(room => room.id === roomId)
				)
			) {
				return this.buildings()[i].id;
			}
		}

		console.error(this.translateService.instant('no_building_found_for_room'));
		return undefined;
	}
}
